import { RootState } from './../rootReducer';
import { RSAA } from 'redux-api-middleware';
import customFailure from 'helpers/customFailureAction';
import delve from 'dlv';

import filter, { SET_FILTERS } from '../reducers/user/filter';
import following, { FollowingActionTypes } from '../reducers/user/following';
import customer, { SET_SELECTED_CUSTOMERS } from '../reducers/user/customer';
import customizeViews, { SET_CUSTOMIZE_VIEW } from './user/customizeView';
import { Dispatch } from 'redux';
import { TGetRootState, TDispatchResult } from 'types/shared';
import * as Sentry from '@sentry/react';
import updatesModal, { SET_UPDATES_MODAL_VIEW } from './user/updatesModal';
import opusAccessBanner, {
  SET_OPUS_ACCESS_BANNER,
} from './user/opusAccessBanner';
import userLocation, { SET_USER_LOCATION } from './user/updateUserLocation';
import userDivision, { SET_USER_DIVISION } from './user/updateUserDivision';
import userLabel, { SET_USER_LABEL } from './user/updateUserLabel';
import userRole, { SET_USER_ROLE } from './user/updateUserRole';
import userHasVisitedVideoPage, {
  SET_USER_HAS_VISITED_VIDEO_PAGE,
} from './user/updateUserHasVisitedVideoPage';
import userIsInterestedInAndroidApp, {
  SET_USER_IS_INTERESTED_IN_ANDROID_APP,
} from './user/updateUsetIsInterestedInAndroidApp';

const GET_USERSETTINGS_REQUEST = 'opus/user/GET_USERSETTINGS_REQUEST';
export const GET_USERSETTINGS_SUCCESS = 'opus/user/GET_USERSETTINGS_SUCCESS';
export const GET_USERSETTINGS_FAILURE = 'opus/user/GET_USERSETTINGS_FAILURE';

const SAVE_USERSETTINGS_REQUEST = 'opus/user/SAVE_USERSETTINGS_REQUEST';
export const SAVE_USERSETTINGS_SUCCESS = 'opus/user/SAVE_USERSETTINGS_SUCCESS';
export const SAVE_USERSETTINGS_FAILURE = 'opus/user/SAVE_USERSETTINGS_FAILURE';

const INITIALIZE_USERSETTINGS_REQUEST =
  'opus/user/INITIALIZE_USERSETTINGS_REQUEST';
const INITIALIZE_USERSETTINGS_SUCCESS =
  'opus/user/INITIALIZE_USERSETTINGS_SUCCESS';
const INITIALIZE_USERSETTINGS_FAILURE =
  'opus/user/INITIALIZE_USERSETTINGS_FAILURE';

/** @todo WRITE TESTS, VALIDATE DATA */

type UserSettingsActionTypes =
  | typeof GET_USERSETTINGS_REQUEST
  | typeof GET_USERSETTINGS_SUCCESS
  | typeof GET_USERSETTINGS_FAILURE
  | typeof SAVE_USERSETTINGS_REQUEST
  | typeof SAVE_USERSETTINGS_SUCCESS
  | typeof SAVE_USERSETTINGS_FAILURE
  | typeof INITIALIZE_USERSETTINGS_REQUEST
  | typeof INITIALIZE_USERSETTINGS_SUCCESS
  | typeof INITIALIZE_USERSETTINGS_FAILURE
  | typeof SET_FILTERS
  | typeof SET_SELECTED_CUSTOMERS
  | FollowingActionTypes
  | typeof SET_CUSTOMIZE_VIEW
  | typeof SET_UPDATES_MODAL_VIEW
  | typeof SET_OPUS_ACCESS_BANNER
  | typeof SET_USER_LOCATION
  | typeof SET_USER_DIVISION
  | typeof SET_USER_LABEL
  | typeof SET_USER_ROLE
  | typeof SET_USER_HAS_VISITED_VIDEO_PAGE
  | typeof SET_USER_IS_INTERESTED_IN_ANDROID_APP;

export interface IUserSettingsAction {
  meta: any;
  type: UserSettingsActionTypes;
  payload: any;
}

interface IHasAll {
  all: boolean;
  [key: string]: boolean;
}

export interface IUserSettingsState {
  loading: IHasAll;
  success: IHasAll;
  error: any;
  errorInfo: any;
  data: any;
}

export const initialState: IUserSettingsState = {
  loading: {
    all: false,
  },
  success: {
    all: false,
  },
  error: {},
  errorInfo: {},
  data: {
    followingEntities: null,
    globalFilters: {
      country: ['all'],
      streaming: ['all'],
      download: ['all'],
      charts: ['all'],
    },
    selectedCustomers: {
      streamingCustomers: [],
      downloadCustomers: [],
    },
    customizeViews: {
      overview_summary_track: null,
      overview_summary_release: null,
      overview_summary_artist: null,
      overview_summary_project: null,
    },
    updatesModalShown: null,
    opusAccessFooterBanner: null,
    userLocation: null,
    userDivision: null,
    userLabel: [],
    userRole: null,
    userHasVisitedVideoPage: false,
    userIsInterestedInAndroidApp: false,
  },
};

const getResponseItem = (response: any, reducerKey: string): any =>
  delve(response, reducerKey);

export default function combinedReducer(
  state: IUserSettingsState = initialState,
  action: IUserSettingsAction
): IUserSettingsState {
  switch (action.type) {
    case INITIALIZE_USERSETTINGS_REQUEST:
    case GET_USERSETTINGS_REQUEST:
      return {
        ...state,
        loading: {
          ...state.loading,
          all: true,
        },
      };

    case SAVE_USERSETTINGS_REQUEST:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.meta.reducerKey]: true,
        },
      };

    case INITIALIZE_USERSETTINGS_SUCCESS:
    case GET_USERSETTINGS_SUCCESS: {
      let newFollowingEntities = action.payload.followingEntities;

      if (!Array.isArray(newFollowingEntities)) {
        Sentry.withScope((scope: any) => {
          scope.setExtras({ action });
          Sentry.captureException(
            'New following entities has an invalid type.'
          );
        });

        newFollowingEntities = [];
      }

      return {
        ...state,
        loading: {
          ...state.loading,
          all: false,
        },
        success: {
          ...state.success,
          all: true,
        },
        error: {
          ...state.error,
          all: null,
        },
        errorInfo: {
          ...state.errorInfo,
          all: null,
        },
        data: {
          globalFilters: {
            ...state.data.globalFilters,
            ...action.payload.globalFilters,
          },
          followingEntities: [
            ...(state.data.followingEntities || []),
            ...newFollowingEntities,
          ],
          selectedCustomers: {
            ...state.data.selectedCustomers,
            ...action.payload.selectedCustomers,
          },
          customizeViews: {
            ...state.data.customizeViews,
            ...action.payload.customizeViews,
          },
          updatesModalShown:
            state.data.updatesModalShown || action.payload.updatesModalShown,
          opusAccessFooterBanner:
            state.data.opusAccessFooterBanner ||
            action.payload.opusAccessFooterBanner,
          userLocation: state.data.userLocation || action.payload.userLocation,
          userDivision: state.data.userDivision || action.payload.userDivision,
          userLabel: [
            ...(state.data.userLabel || []),
            ...(action.payload.userLabel || []),
          ],
          userRole: state.data.userRole || action.payload.userRole,
          userHasVisitedVideoPage:
            state.data.userHasVisitedVideoPage ||
            action.payload.userHasVisitedVideoPage,
          userIsInterestedInAndroidApp:
            state.data.userIsInterestedInAndroidApp ||
            action.payload.userIsInterestedInAndroidApp,
        },
      };
    }

    case SAVE_USERSETTINGS_SUCCESS:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.meta.reducerKey]: false,
        },
        success: {
          ...state.success,
          [action.meta.reducerKey]: true,
        },
        error: {
          ...state.error,
          [action.meta.reducerKey]: null,
        },
        errorInfo: {
          ...state.errorInfo,
          [action.meta.reducerKey]: null,
        },
        data: {
          ...state.data,
          [action.meta.reducerKey]: getResponseItem(
            action.payload,
            action.meta.reducerKey
          ),
        },
      };

    case INITIALIZE_USERSETTINGS_FAILURE:
    case GET_USERSETTINGS_FAILURE:
      return {
        ...state,
        loading: {
          ...state.loading,
          all: false,
        },
        success: {
          ...state.success,
          all: false,
        },
        error: {
          ...state.error,
          all: new Error(action.payload.error),
        },
        errorInfo: {
          ...state.errorInfo,
          all: action.payload.errorInfo,
        },
      };

    case SAVE_USERSETTINGS_FAILURE:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.meta.reducerKey]: false,
        },
        success: {
          ...state.success,
          [action.meta.reducerKey]: false,
        },
        error: {
          ...state.error,
          [action.meta.reducerKey]: new Error(action.payload.error),
        },
        errorInfo: {
          ...state.errorInfo,
          [action.meta.reducerKey]: action.payload.errorInfo,
        },
        data: {
          ...state.data,
          [action.meta.reducerKey]: action.meta.originalState
            ? action.meta.originalState
            : state.data[action.meta.reducerKey],
        },
      };

    default:
      return {
        ...state,
        data: {
          globalFilters: filter(state.data.globalFilters, action),
          followingEntities: following(state.data.followingEntities, action),
          selectedCustomers: customer(state.data.selectedCustomers, action),
          customizeViews: customizeViews(state.data.customizeViews, action),
          updatesModalShown: updatesModal(state.data.updatesModalShown, action),
          opusAccessFooterBanner: opusAccessBanner(
            state.data.opusAccessFooterBanner,
            action
          ),
          userLocation: userLocation(state.data.userLocation, action),
          userDivision: userDivision(state.data.userDivision, action),
          userLabel: userLabel(state.data.userLabel, action),
          userRole: userRole(state.data.userRole, action),
          userHasVisitedVideoPage: userHasVisitedVideoPage(
            state.data.userHasVisitedVideoPage,
            action
          ),
          userIsInterestedInAndroidApp: userIsInterestedInAndroidApp(
            state.data.userIsInterestedInAndroidApp,
            action
          ),
        },
      };
  }
}

export function shouldLoadUserSettings(): (
  dispatch: Dispatch,
  getState: () => RootState
) => boolean {
  return function (dispatch: Dispatch, getState: () => RootState): boolean {
    const loadingStates = Object.values<boolean>(
      getState().userSettings.loading
    );
    const loading = loadingStates.length > 0 && loadingStates.every(Boolean);
    const successStates = Object.values<boolean>(
      getState().userSettings.success
    );
    const success = successStates.length > 0 && successStates.every(Boolean);
    return !loading && !success;
  };
}

/** Gets the user settings object from the server. */
export const getUserSettings = (): any => {
  return async function (
    dispatch: Dispatch,
    getState: TGetRootState
  ): Promise<any> {
    const { id } = getState().authentication.profile;

    const response = await dispatch({
      [RSAA]: {
        types: [
          { type: GET_USERSETTINGS_REQUEST },
          { type: GET_USERSETTINGS_SUCCESS },
          { type: GET_USERSETTINGS_FAILURE },
        ],
        endpoint: `/kv/user_${id}`,
        method: 'GET',
        credentials: 'same-origin',
      },
    });

    return response;
  };
};

/** If no settings object is found on the server we set a basic structure based on the initialState. */
export const initializeUserSettings = (): any => {
  return async function (
    dispatch: Dispatch,
    getState: TGetRootState
  ): Promise<any> {
    const { id: userId } = getState().authentication.profile;
    const payload = initialState.data;

    return await dispatch({
      [RSAA]: {
        types: [
          {
            type: INITIALIZE_USERSETTINGS_REQUEST,
          },
          {
            type: INITIALIZE_USERSETTINGS_SUCCESS,
            payload,
          },
          {
            type: INITIALIZE_USERSETTINGS_FAILURE,
            payload: async (
              action: IUserSettingsAction,
              state: IUserSettingsState,
              res: any
            ): Promise<any> => await customFailure(res),
          },
        ],
        endpoint: `/kv/user_${userId}`,
        method: 'PUT',
        body: JSON.stringify(payload),
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      },
    });
  };
};

export function loadOrInitUserSettings() {
  return async function (dispatch: Dispatch): Promise<any> {
    const response = await dispatch(getUserSettings());

    if (response.error) {
      if (response.payload.status === 404) {
        return await dispatch(initializeUserSettings());
      }
    }

    return response;
  };
}

/**
 * Sends the complete data reducerKey to the API
 */
export const saveUserSettings = (
  reducerKey: string,
  originalState?: any
): TDispatchResult<any> => {
  return function (dispatch: Dispatch, getState: TGetRootState): any {
    const { id } = getState().authentication.profile;
    const payload = getState().userSettings.data;

    // We're preventing dispatcher from even getting here in the first place, but in the
    // scenario that we do allow a user to call saveUserSettings by accident, we return a hard error here.
    if (!getState().userSettings.success.all) {
      throw new Error(
        'You are trying to save user settings to the server, but we found an error in our local copy of your settings object. We will block this request.'
      );
    }

    return dispatch({
      [RSAA]: {
        types: [
          { type: SAVE_USERSETTINGS_REQUEST, meta: { reducerKey } },
          { type: SAVE_USERSETTINGS_SUCCESS, payload, meta: { reducerKey } },
          {
            type: SAVE_USERSETTINGS_FAILURE,
            meta: { reducerKey, originalState },
            payload: async (
              action: IUserSettingsAction,
              state: IUserSettingsState,
              res: any
            ): Promise<any> => {
              const failurePayload = await customFailure(res);
              return failurePayload;
            },
          },
        ],
        endpoint: `/kv/user_${id}`,
        method: 'PUT',
        body: JSON.stringify(payload),
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      },
    });
  };
};
